import { useListOwnersAssignedUsersTasks, ListOwnerAssignments } from "@/lib/api/knowledge";
import { GenerateLoadingOrError } from "@/lib/utils";
import { CheckCircle, Clock, PenSquare } from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { DataTable, DataTableColumn } from "@/components/ui/DataTable/index";

export function OwnersAssignedOutReport() {
  const navigate = useNavigate();
  const assignedOutListRes = useListOwnersAssignedUsersTasks();

  const tableData = useMemo(() => {
    if (!assignedOutListRes.data?.success || !assignedOutListRes.data?.data) {
      return [];
    }

    return assignedOutListRes.data.data || [];
  }, [assignedOutListRes.data?.data]);

  const loadingOrErrorUi = GenerateLoadingOrError([assignedOutListRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const columns: DataTableColumn<ListOwnerAssignments>[] = [
    {
      id: "assignee_name",
      accessorKey: "assignee_name",
      header: "Assignee",
      accessorFn: (row) => row.Assignee?.name || "",
      cell: (info) => info.getValue() as string,
      minSize: 150,
      maxSize: 250,
    },
    {
      id: "task_title",
      accessorKey: "task_title",
      header: "Task",
      accessorFn: (row) => row.KnowledgeItem?.title || "",
      cell: (info) => {
        const row = info.row.original;
        return (
          <span 
            className="underline decoration-solid decoration-sky-500 cursor-pointer"
            onClick={() => navigate(`/tasks/${row.knowledge_item_id}`)}
          >
            {info.getValue() as string}
          </span>
        );
      },
      minSize: 200,
      maxSize: 400,
    },
    {
      id: "active_step",
      accessorKey: "active_step",
      header: "Active Step",
      accessorFn: (row) => row.active_step?.title || "N/A",
      cell: (info) => info.getValue() as string,
      minSize: 150,
      maxSize: 300,
    },
    {
      id: "step_progress",
      accessorKey: "step_progress",
      header: "Step Progress",
      accessorFn: (row) => {
        if (row.status === 'COMPLETED') {
          return `${row.active_step.order + 1} / ${row.KnowledgeItem?.steps?.length}`;
        }
        if (row.status === 'PENDING' || !row.active_step) {
          return `0 / ${row.active_step.order + 1}`;
        }
        return `${row.active_step.order + 1} / ${row.KnowledgeItem?.steps?.length}`;
      },
      cell: (info) => info.getValue() as string,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue() as string;
        return (
          <Badge
            variant="outline"
            className={
              status === 'COMPLETED'
                ? "bg-green-50 text-green-700 border-green-200"
                : status === 'IN_PROGRESS'
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
            }
          >
            {status === 'COMPLETED' && <CheckCircle className="h-3 w-3 mr-1" />}
            {status === 'IN_PROGRESS' && <PenSquare className="h-3 w-3 mr-1" />}
            {status === 'PENDING' && <Clock className="h-3 w-3 mr-1" />}
            {status.replace('_', ' ')}
          </Badge>
        );
      },
      minSize: 120,
      maxSize: 180,
    },
  ];

  return (
    <div className="p-4 space-y-6">
      <DataTable
        className="shadow-lg"
        stickyHeaders
        tableClassName="max-h-[calc(100vh-300px)] overflow-y-scroll"
        data={tableData}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        groupBy={["assignee_name"]}
        filterStyle='select'
        filterMode='internal'
        emptyMessage={assignedOutListRes.isLoading ? "Loading..." : "No Assigned Out Tasks"}
      />
    </div>
  );
}
